import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import {
  QuoteDTO,
  QuoteSectionDTO,
  createQuoteV2SectionMutation
} from '@setvi/shared/services/react-query';
import { generateNextPosition, updateItemInTree } from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface AddSectionVariables {
  name: string;
  description?: string;
}

/**
 * Add section optimistically to quote
 */
const addSectionOptimistically = (
  quote: QuoteDTO | undefined,
  { name, description }: AddSectionVariables,
  position: string,
  tempId: string
): QuoteDTO | undefined => {
  if (!quote) return quote;

  const newSection = {
    id: tempId,
    type: 'section',
    name,
    note: description || null,
    children: [],
    position
  } as unknown as QuoteSectionDTO;

  return {
    ...quote,
    items: [...quote.items, newSection]
  };
};

/**
 * Add section to quote using v2 section endpoint
 * Section appears immediately in UI before API confirmation
 */
export const useAddSection = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = createQuoteV2SectionMutation();

  return useMutation({
    mutationFn: ({ name, description }: AddSectionVariables) => {
      const items = cache.getItems() || [];
      const position = generateNextPosition(items);

      return mutationFn({
        quoteId,
        name,
        note: description || '',
        position
      });
    },

    onMutate: async (variables: AddSectionVariables) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();
      const previousItems = cache.getItems();
      const tempId = uuidv4();

      // Compute position from pre-optimistic data so it's included on the optimistic item
      const position = generateNextPosition(previousItems || []);

      const composed = previousQuote
        ? ({
            ...previousQuote,
            items: previousItems || []
          } as unknown as QuoteDTO)
        : undefined;

      const updated = addSectionOptimistically(
        composed,
        variables,
        position,
        tempId
      );

      if (updated) {
        const { items: updatedItems, ...quoteData } = updated as any;
        cache.setQuote(() => quoteData);
        cache.setItems(() => updatedItems);
      }

      return { previousQuote, previousItems, tempId };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: (data, _variables, context) => {
      const res = data as any;
      const serverId = res?.item?._id || res?._id;
      if (serverId && context?.tempId) {
        cache.setItems(old =>
          old
            ? updateItemInTree(old, context.tempId, {
                _id: serverId,
                id: serverId
              })
            : old
        );
      }
      cache.invalidate();
    }
  });
};
